import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitElement } from 'lit';
import { store } from '../store.js';
import type { MessageKey } from '../i18n/messages.js';
import { pageStyles } from '../styles/page.js';
import { EXTERNAL } from '@healthgeek/utils';
import { entryMali, entryJuvo, entryDental } from '../illustrations.js';
import '@healthgeek/ui';

class HgPage extends LitElement {
  static styles = [pageStyles];

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
    window.scrollTo(0, 0);
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }

  protected firstUpdated() {
    requestAnimationFrame(() => this.scrollHash());
  }

  protected scrollHash() {
    const id = location.hash.replace('#', '');
    if (!id) return;
    const el = this.renderRoot.querySelector(`#${CSS.escape(id)}`);
    if (el instanceof HTMLElement) el.scrollIntoView({ block: 'start' });
  }

  protected unsubscribe?: () => void;
  protected t(key: MessageKey) {
    return store.t(key);
  }
}

@customElement('hg-mission-page')
export class HgMissionPage extends HgPage {
  render() {
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">${this.t('pillars.kicker')}</div>
          <h1>${this.t('page.mission.title')}</h1>
          <p class="first">${this.t('story.p1')}</p>
          <p>${this.t('story.p2')}</p>
          <p>${this.t('story.p3')}</p>
        </div>
      </section>
      <section class="section">
        <div class="wrap">
          <div class="section-num">${this.t('story.vision.section')}</div>
          <h2>${this.t('story.vision.title')}</h2>
          <p>${this.t('story.vision.body')}</p>
          <blockquote class="pull"><p>“${this.t('story.vision.quote')}”</p></blockquote>
        </div>
      </section>
      <section class="section no-border">
        <div class="wrap">
          <div class="section-num">${this.t('story.rules.section')}</div>
          <h2>${this.t('story.rules.title')}</h2>
          <div class="value-entry">
            <div class="n">${this.t('pillars.1.n')}</div>
            <h3>${this.t('pillars.1.title')}</h3>
            <p>${this.t('pillars.1.body')}</p>
          </div>
          <div class="value-entry">
            <div class="n">${this.t('pillars.2.n')}</div>
            <h3>${this.t('pillars.2.title')}</h3>
            <p>${this.t('pillars.2.body')}</p>
          </div>
        </div>
      </section>
    `;
  }
}

@customElement('hg-programs-page')
export class HgProgramsPage extends HgPage {
  render() {
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">${this.t('programs.kicker')}</div>
          <h1>${this.t('programs.page.title')}</h1>
          <p>${this.t('programs.lede')}</p>
        </div>
      </section>

      <div class="program-entry" id="mali">
        <div class="wrap entry-grid">
          <div>
            <div class="program-tag">${this.t('programs.mali.tag')}</div>
            <h2>${this.t('programs.mali.headline')}</h2>
            <p>${this.t('programs.mali.body')}</p>
            <div class="who">${this.t('programs.mali.who')}</div>
            <div class="meta-row">
              <div><span>${this.t('programs.mali.meta1')}</span>${this.t('programs.mali.meta1l')}</div>
              <div><span>${this.t('programs.mali.meta2')}</span>${this.t('programs.mali.meta2l')}</div>
            </div>
            <a class="p-link" href="/story">${this.t('programs.mali.link')} →</a>
          </div>
          <div class="illus-card">${entryMali}</div>
        </div>
      </div>

      <div class="program-entry" id="juvo">
        <div class="wrap entry-grid">
          <div>
            <div class="program-tag">${this.t('programs.juvo.tag')}</div>
            <h2>${this.t('programs.juvo.headline')}</h2>
            <p>${this.t('programs.juvo.body')}</p>
            <div class="who">${this.t('programs.juvo.who')}</div>
            <div class="meta-row">
              <div><span>${this.t('programs.juvo.meta1')}</span>${this.t('programs.juvo.meta1l')}</div>
              <div><span>${this.t('programs.juvo.meta2')}</span>${this.t('programs.juvo.meta2l')}</div>
            </div>
            <a class="p-link" href="${EXTERNAL.juvo}" target="_blank" rel="noopener">${this.t('programs.juvo.link')} →</a>
          </div>
          <div class="illus-card">${entryJuvo}</div>
        </div>
      </div>

      <div class="program-entry" id="dental-intelligence">
        <div class="wrap entry-grid">
          <div>
            <div class="program-tag">${this.t('programs.di.tag')}</div>
            <h2>${this.t('programs.di.headline')}</h2>
            <p>${this.t('programs.di.body')}</p>
            <div class="who">${this.t('programs.di.who')}</div>
            <div class="meta-row">
              <div><span>${this.t('programs.di.meta1')}</span>${this.t('programs.di.meta1l')}</div>
              <div><span>${this.t('programs.di.meta2')}</span>${this.t('programs.di.meta2l')}</div>
            </div>
            <a class="p-link" href=${EXTERNAL.dental} target="_blank" rel="noopener">${this.t('programs.di.link')} →</a>
          </div>
          <div class="illus-card">${entryDental}</div>
        </div>
      </div>
    `;
  }
}

@customElement('hg-program-detail-page')
export class HgProgramDetailPage extends HgPage {
  @property() slug = '';

  render() {
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">${this.t('programs.mali.tag')}</div>
          <h1>${this.t('programs.mali.headline')}</h1>
          <p class="first">${this.t('programs.mali.body')}</p>
          <div class="who">${this.t('programs.mali.who')}</div>
          <div class="actions">
            <hg-button href="/contact">${this.t('nav.involved')}</hg-button>
            <hg-button href="/programs" variant="outlined">${this.t('nav.programs')}</hg-button>
          </div>
        </div>
      </section>
    `;
  }
}

@customElement('hg-impact-page')
export class HgImpactPage extends HgPage {
  render() {
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">${this.t('impact.kicker')}</div>
          <h1>${this.t('page.impact.title')}</h1>
          <p>${this.t('impact.lede')}</p>
        </div>
      </section>
      <section class="section no-border">
        <div class="wrap-wide">
          <div class="counters-grid">
            <hg-stat icon="local_hospital" label=${this.t('impact.clinics.label')} value="12"></hg-stat>
            <hg-stat icon="groups" label=${this.t('impact.chws.label')} value="27"></hg-stat>
            <hg-stat icon="memory" label=${this.t('impact.local.label')} value="257"></hg-stat>
            <hg-stat icon="public" label=${this.t('impact.regions.label')} value="2"></hg-stat>
          </div>
        </div>
      </section>
    `;
  }
}

@customElement('hg-where-page')
export class HgWherePage extends HgPage {
  render() {
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">${this.t('geo.cap1')}</div>
          <h1>${this.t('geo.title.1')} ${this.t('geo.title.em')} ${this.t('geo.title.2')}</h1>
          <p>${this.t('geo.cap2')}</p>
        </div>
      </section>
      <section class="section no-border">
        <div class="wrap">
          ${[
            ['geo.us.title', 'geo.us.body'],
            ['geo.p1.title', 'geo.p1.body'],
            ['geo.p2.title', 'geo.p2.body'],
            ['geo.p3.title', 'geo.p3.body'],
          ].map(
            ([title, body], i) => html`
              <div class="value-entry">
                <div class="n">0${i + 1}</div>
                <h3>${this.t(title as MessageKey)}</h3>
                <p>${this.t(body as MessageKey)}</p>
              </div>
            `,
          )}
        </div>
      </section>
    `;
  }
}

@customElement('hg-governance-page')
export class HgGovernancePage extends HgPage {
  render() {
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">${this.t('gov.kicker')}</div>
          <h1>${this.t('gov.title')}</h1>
          <p>${this.t('gov.lede')}</p>
        </div>
      </section>
      <section class="section">
        <div class="wrap">
          <div class="section-num">${this.t('gov.board.section')}</div>
          <h2>${this.t('gov.board.title')}</h2>
          <table class="roster">
            ${BOARD.map(
              (row) => html`
                <tr>
                  <td>${row.name}</td>
                  <td class="role">${row.role}</td>
                </tr>
              `,
            )}
          </table>
        </div>
      </section>
      <section class="section">
        <div class="wrap">
          <div class="section-num">${this.t('gov.cac.section')}</div>
          <h2>${this.t('gov.cac.title')}</h2>
          <p>${this.t('gov.cac.body')}</p>
        </div>
      </section>
      <section class="section no-border">
        <div class="wrap">
          <div class="section-num">${this.t('gov.committees.section')}</div>
          <h2>${this.t('gov.committees.title')}</h2>
          <div class="committee">
            <h3>${this.t('gov.med.title')}</h3>
            <p>${this.t('gov.med.body')}</p>
          </div>
          <div class="committee">
            <h3>${this.t('gov.tech.title')}</h3>
            <p>${this.t('gov.tech.body')}</p>
          </div>
        </div>
      </section>
    `;
  }
}

const BOARD = [
  { name: 'Dr. Miah Ramsey', role: 'Chairwoman & Executive Director' },
  { name: 'Vice President', role: 'To be appointed — public health background sought' },
  { name: 'Seb Diarra', role: 'Secretary & Director of Programs, Mali' },
  { name: 'Boubacar Sidibe', role: 'Treasurer' },
  { name: 'Aminata Sissoko', role: 'Communications & Outreach Director' },
  { name: 'Dr. Ramata Sissoko Cisse', role: 'Research & Programs Director' },
  { name: 'Grants & Fundraising Director', role: 'To be appointed' },
];

@customElement('hg-mou-robotsmali-page')
export class HgMouRobotsMaliPage extends HgPage {
  static styles = [
    pageStyles,
    css`
      .mou-meta-bar {
        background: var(--hg-paper-2);
        border-bottom: 1px solid var(--hg-line);
        padding: 18px 0;
      }
      .mou-meta-inner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        flex-wrap: wrap;
      }
      .mou-meta-info {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .mou-meta-title {
        font-weight: 700;
        font-size: 14px;
        color: var(--hg-ink);
        letter-spacing: 0.5px;
      }
      .mou-meta-sub {
        font-size: 12px;
        color: var(--hg-muted);
      }
      .mou-actions {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
      }
      .mou-frame-container {
        width: 100%;
        min-height: calc(100vh - 220px);
        background: #e6e8e6;
        padding: 24px 0 36px;
      }
      .mou-frame {
        width: 100%;
        max-width: 920px;
        height: 82vh;
        min-height: 720px;
        margin: 0 auto;
        display: block;
        border: 1px solid var(--hg-line);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
        background: #ffffff;
        border-radius: 4px;
      }
      @media (max-width: 899px) {
        .mou-meta-inner {
          flex-direction: column;
          align-items: flex-start;
          gap: 12px;
        }
        .mou-actions {
          width: 100%;
        }
        .mou-actions hg-button {
          flex: 1;
        }
        .mou-frame-container {
          padding: 12px 8px 24px;
        }
        .mou-frame {
          height: 78vh;
          min-height: 560px;
        }
      }
      @media (max-width: 640px) {
        .hero {
          padding: 32px 0 24px;
        }
        .hero h1 {
          font-size: 26px;
        }
        .hero .lede {
          font-size: 14px;
          line-height: 1.5;
        }
        .mou-meta-bar {
          padding: 12px 0;
        }
        .mou-actions {
          flex-direction: column;
          width: 100%;
        }
        .mou-actions hg-button {
          width: 100%;
        }
        .mou-frame-container {
          padding: 8px 4px 16px;
        }
        .mou-frame {
          height: 72vh;
          min-height: 440px;
          border-radius: 0;
        }
      }
    `,
  ];

  render() {
    return html`
      <section class="hero">
        <div class="wrap">
          <div class="kicker">STRATEGIC PARTNERSHIP // ACCORD DE COLLABORATION</div>
          <h1>HealthGeek &amp; RobotsMali MoU</h1>
          <p class="lede">
            FRAMEWORK FOR STRATEGIC COLLABORATION IN DIGITAL HEALTH, ARTIFICIAL INTELLIGENCE, RESEARCH, EDUCATION &amp; HEALTH TECHNOLOGY INNOVATION
          </p>
        </div>
      </section>

      <section class="mou-meta-bar">
        <div class="wrap-wide mou-meta-inner">
          <div class="mou-meta-info">
            <span class="mou-meta-title">MEMORANDUM OF UNDERSTANDING (OFFICIAL DOCUMENT)</span>
            <span class="mou-meta-sub">HealthGeek Initiative &amp; RobotsMali AI4D Laboratory &bull; Bamako, Mali</span>
          </div>
          <div class="mou-actions">
            <hg-button
              variant="outline"
              href="/HealthGeek_RobotsMali_MoU.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Full Window
            </hg-button>
            <hg-button
              variant="secondary"
              href="/HealthGeek_RobotsMali_MoU.html"
              download="HealthGeek_RobotsMali_MoU.html"
            >
              Download MoU
            </hg-button>
          </div>
        </div>
      </section>

      <section class="mou-frame-container">
        <iframe
          class="mou-frame"
          src="/HealthGeek_RobotsMali_MoU.html"
          title="HealthGeek and RobotsMali Memorandum of Understanding"
        ></iframe>
      </section>
    `;
  }
}

